// IndexTVPage.js — versão TV/BROADCAST dos índices (RRTV)
// ============================================================
// Board de índices LIMPO pra vídeo/OBS: sem Header, sem nav, fundo TRANSPARENTE,
// TV-sized (1920×1080). Todo show abre com índice — este é o componente do topo.
// DETERMINÍSTICO: quem manda a troca é o cue/TSV via screenshot, não timer interno.
//
// Rota sugerida (app.js):
//   <Route path="/tv/index" element={<IndexTVPage/>} />
//
// CONTROLES (query params — parametrico pro cue na hora de gravar):
//   ?zone=b3|us|euro|asia|world   preset de símbolos (default b3)
//   ?symbols=^BVSP,^GSPC,^N225    lista explícita (override do zone)
//   ?mode=hero|board|row          layout: 1 grande | grade | fileira (default board)
//   ?highlight=^BVSP              destaca um card (borda roxa, escala maior)
//   ?title=Índices%20Globais      título opcional no topo
//   ?max=6                        limita quantos cards
//   ?sort=rate|abs|none           ordena por variação (default none = ordem do preset)
//
// Endpoint: constante abaixo. Troque INDEX_URL pra fonte da SUA máquina de deploy
// (o shape esperado está em mapRow(); adapte lá se os campos diferirem).

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// >>> AJUSTE AQUI na máquina de deploy <<<
const INDEX_URL = process.env.REACT_APP_INDEX_URL || 'https://magma.relatorioreservado.com.br/api/go82/api/indices';

const ROXO = '#6660fe';   // RR Magma roxo
const INK = '#0e0d1f';
const UP = '#22c55e';
const DOWN = '#ef4444';
const MUTED = '#8b8ba7';

// presets por zona (o cue escolhe qual mostrar)
const ZONES = {
  b3:    ['^BVSP'],
  us:    ['^DJI', '^GSPC', '^IXIC', '^RUT'],
  euro:  ['^GDAXI', '^FCHI', '^FTSE', '^STOXX50E'],
  asia:  ['^N225', '^HSI', '^KS11', '000001.SS'],
  world: ['^BVSP', '^GSPC', '^IXIC', '^GDAXI', '^FTSE', '^N225', '^HSI'],
};

// nome curto e bonito por símbolo (fallback pro name do endpoint)
const PRETTY = {
  '^BVSP': 'Ibovespa', '^DJI': 'Dow Jones', '^GSPC': 'S&P 500', '^IXIC': 'Nasdaq',
  '^RUT': 'Russell 2000', '^GDAXI': 'DAX', '^FCHI': 'CAC 40', '^FTSE': 'FTSE 100',
  '^STOXX50E': 'Euro Stoxx 50', '^N225': 'Nikkei 225', '^HSI': 'Hang Seng',
  '^KS11': 'Kospi', '000001.SS': 'Shanghai',
};

// adapte este mapper se o shape do seu endpoint diferir
function mapRow(x) {
  return {
    symbol: x.symbol,
    name: PRETTY[x.symbol] || x.name || x.symbol,
    value: x.last_quote ?? x.close ?? x.price,
    rate: x.rate ?? x.pct_change ?? x.changePercent,
  };
}

const fmt = (n, dec = 2) =>
  (n == null || isNaN(n)) ? '—'
    : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const pct = (r) => (r == null ? '—' : `${r > 0 ? '+' : ''}${fmt(r)}%`);
const arrow = (r) => (r > 0 ? '▲' : r < 0 ? '▼' : '■');
const col = (r) => (r > 0 ? UP : r < 0 ? DOWN : MUTED);

function useIndices() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(INDEX_URL);
        const d = await r.json();
        const arr = Array.isArray(d) ? d : (d.indices || d.data || []);
        if (alive) setRows(arr.map(mapRow));
      } catch { /* deploy machine wires the real URL */ }
    })();
    return () => { alive = false; };
  }, []);
  return rows;
}

// ---- um card de índice ----
function Card({ it, big, highlight }) {
  const c = col(it.rate);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: big ? '48px 60px' : '28px 34px',
      borderRadius: 22,
      background: 'rgba(20,19,42,0.72)',
      border: `2px solid ${highlight ? ROXO : 'rgba(255,255,255,0.08)'}`,
      boxShadow: highlight ? `0 0 40px ${ROXO}44` : '0 8px 30px rgba(0,0,0,0.35)',
      backdropFilter: 'blur(6px)',
      transform: highlight ? 'scale(1.04)' : 'none',
      minWidth: big ? 620 : 300,
    }}>
      <div style={{
        fontFamily: 'Georgia, serif', fontWeight: 700, color: ROXO,
        fontSize: big ? 46 : 26, letterSpacing: '-0.02em', marginBottom: 6,
      }}>{it.name}</div>
      <div style={{
        fontFamily: 'monospace', color: MUTED, fontSize: big ? 20 : 14,
        letterSpacing: '0.12em', marginBottom: big ? 22 : 12,
      }}>{it.symbol.replace('^', '')}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: big ? 28 : 16 }}>
        <span style={{
          fontFamily: 'monospace', fontWeight: 700, color: '#fff',
          fontSize: big ? 84 : 40, lineHeight: 1,
        }}>{fmt(it.value, it.value >= 1000 ? 0 : 2)}</span>
        <span style={{
          fontFamily: 'monospace', fontWeight: 700, color: c,
          fontSize: big ? 52 : 28,
        }}>{arrow(it.rate)} {pct(it.rate)}</span>
      </div>
    </div>
  );
}

export default function IndexTVPage() {
  const [params] = useSearchParams();
  const zone = (params.get('zone') || 'b3').toLowerCase();
  const mode = (params.get('mode') || 'board').toLowerCase();
  const highlight = params.get('highlight') || '';
  const title = params.get('title') || '';
  const max = Number(params.get('max')) || 0;
  const sort = (params.get('sort') || 'none').toLowerCase();
  const explicit = (params.get('symbols') || '').split(',').map(s => s.trim()).filter(Boolean);

  const rows = useIndices();
  const wanted = explicit.length ? explicit : (ZONES[zone] || ZONES.b3);

  const items = useMemo(() => {
    const by = Object.fromEntries(rows.map(r => [r.symbol, r]));
    let list = wanted.map(s => by[s]).filter(Boolean);
    if (sort === 'rate') list = [...list].sort((a, b) => (b.rate ?? -9) - (a.rate ?? -9));
    if (sort === 'abs') list = [...list].sort((a, b) => Math.abs(b.rate ?? 0) - Math.abs(a.rate ?? 0));
    if (max) list = list.slice(0, max);
    return list;
  }, [rows, wanted.join(','), sort, max]);

  const isHero = mode === 'hero' || items.length === 1;
  const isRow = mode === 'row';

  return (
    <div data-zone={zone} data-mode={mode} style={{
      width: 1920, height: 1080, background: 'transparent',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'center', alignItems: 'center', gap: 40,
      padding: 80, boxSizing: 'border-box', overflow: 'hidden',
    }}>
      {title ? (
        <div style={{
          fontFamily: 'Georgia, serif', fontWeight: 700, color: '#fff',
          fontSize: 44, letterSpacing: '-0.02em',
          borderBottom: `3px solid ${ROXO}`, paddingBottom: 12,
        }}>{title}</div>
      ) : null}

      {isHero ? (
        <Card it={items[0] || { name: '—', symbol: '', value: null, rate: null }} big highlight />
      ) : (
        <div style={{
          display: isRow ? 'flex' : 'grid',
          gridTemplateColumns: `repeat(${Math.min(items.length, items.length > 4 ? 4 : items.length) || 1}, 1fr)`,
          gap: 34, flexWrap: isRow ? 'nowrap' : 'wrap',
          justifyContent: 'center', alignItems: 'stretch',
        }}>
          {items.map(it => (
            <Card key={it.symbol} it={it} highlight={it.symbol === highlight} />
          ))}
        </div>
      )}
    </div>
  );
}
